import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { H6 } from "../../Typography";
import Avatar from "../../Avatar";
import { Link, useLocation } from "react-router-dom";
import Flex from "../../Flex";
import themeGet from "@styled-system/theme-get";
import {
  sharedNavItemActiveStyles,
  sharedNavItemBaseStyles,
  sharedMenuItemContainerStyles
} from "../styles/sharedHoverStyles";
const createShouldForwardProp = (blockedProps) =>
  function shouldForwardProp(prop, defaultValidatorFn) {
    if (blockedProps.includes(prop)) {
      return false;
    }

    if (typeof defaultValidatorFn === "function") {
      return defaultValidatorFn(prop);
    }

    return true;
  };

const ItemList = styled("ul").withConfig({
  shouldForwardProp: createShouldForwardProp(["isExpanded"])
})`
  list-style: none;
  padding: 0;
  margin: 0;
  align-items: ${(props) => (props.isExpanded ? "flex-start" : "center")};
  text-align: ${(props) => (props.isExpanded ? "left" : "center")};
  display: flex;
  flex-direction: column;
  gap: ${(props) => themeGet("space.xs")(props)};
`;

const ItemName = styled.span`
  font-size: ${(props) => themeGet("fontSizes.1")(props)};
`;
export const Item = styled("li").withConfig({
  shouldForwardProp: createShouldForwardProp(["isExpanded", "alignRight"])
})`
  ${sharedMenuItemContainerStyles}
  gap: 10px;

  justify-content: ${(props) => {
    if (props.isExpanded) {
      return "space-between";
    }
    if (props.alignRight) {
      return "flex-end";
    }
    return "center";
  }};

  a {
    display: flex;
    gap: 10px;
    align-items: center;
    font-size: ${(props) => themeGet("fontSizes.1")(props)};
    text-decoration: none;
    padding: 0;
    flex: 1;
    background-color: transparent;
    ${sharedNavItemBaseStyles}
    ${sharedNavItemActiveStyles}
  }
`;
export const AvatarLink = ({ avatar, name, link, shape = "square", showName, gradient }) => {
  const location = useLocation();
  // Check if current path starts with the link path to handle nested routes
  const isActive = link && location.pathname.startsWith(link);

  return (
    <Link
      to={link || "#"}
      aria-label={name}
      className={isActive ? "active" : ""}
      style={!showName ? { marginLeft: "-2px" } : undefined}
    >
      <Avatar image={avatar} customSize="24px" shape={shape} gradient={gradient} />
      {showName && <ItemName>{name}</ItemName>}
    </Link>
  );
};

AvatarLink.propTypes = {
  avatar: PropTypes.string,
  name: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  shape: PropTypes.string,
  showName: PropTypes.bool,
  gradient: PropTypes.string
};

/**
 * Generic section with optional title and a list of items rendered via renderItem.
 * Used by SideNavTeamsSection and SideNavPinnedSection.
 */
const BaseSection = ({
  title,
  items,
  isExpanded,
  renderItem,
  display = ["none", "none", "none", "flex"]
}) => {
  // Filter out isExpanded prop to prevent it from being passed to DOM
  const flexProps = {
    flexDirection: "column",
    gap: "xs",
    my: isExpanded ? "xxs" : "xs",
    display
  };

  return (
    <Flex {...flexProps}>
      {isExpanded && (
        <H6 sizing="small" color="greyDark">
          {title}
        </H6>
      )}
      <ItemList isExpanded={isExpanded}>
        {items.map((item) => renderItem(item, isExpanded))}
      </ItemList>
    </Flex>
  );
};

BaseSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  isExpanded: PropTypes.bool,
  renderItem: PropTypes.func.isRequired,
  display: PropTypes.array
};

export default BaseSection;
